import { enhancedApi } from '@store/generated/jotaudit';

/**
 * Feature-specific tag types for jotaudit
 */
const JOTAUDIT_TAG_TYPES = [
  'NoteAudit',
  'NoteAuditList',
  'Provider',
  'ProviderList',
  'AuditRevision',
  'AuditRevisionList',
  'Organization',
  'OrganizationList',
  'GlobalStats',
] as const;

/**
 * JotAudit API - extends the generated API with custom tagging
 */
export const jotauditApi = enhancedApi.enhanceEndpoints({
  addTagTypes: JOTAUDIT_TAG_TYPES,
  endpoints: {
    // Provider endpoints
    // @ts-ignore - Override query function to accept is_completed param until OpenAPI spec is updated
    getV2ProviderJotauditDashboardNotes: {
      query: (queryArg?: { is_completed?: boolean }) => ({
        url: `/v2/provider/jotaudit/dashboard/notes`,
        params: queryArg?.is_completed !== undefined ? { is_completed: queryArg.is_completed } : {},
      }),
      providesTags: (result: any) =>
        result && result.audits
          ? [
              ...result.audits.map((item: any) => ({
                type: 'NoteAudit' as const,
                id: item.audit?.note_audit_id || 'unknown',
              })),
              { type: 'NoteAuditList' as const, id: 'LIST' },
            ]
          : [{ type: 'NoteAuditList', id: 'LIST' }],
    },
    getV2ProviderJotauditDashboardNotesByNoteId: {
      providesTags: (result, _error, { noteId }) =>
        result && result.audit
          ? [
              { type: 'NoteAudit' as const, id: result.audit.note_audit_id },
              { type: 'NoteAuditList' as const, id: 'LIST' },
            ]
          : [{ type: 'NoteAudit', id: noteId }],
    },
    getV2ProviderJotauditDashboardProviders: {
      providesTags: result =>
        result && result.providers
          ? [
              ...result.providers.map(item => ({
                type: 'Provider' as const,
                id: item.user_id || 'unknown',
              })),
              { type: 'ProviderList' as const, id: 'LIST' },
            ]
          : [{ type: 'ProviderList', id: 'LIST' }],
    },
    getV2ProviderJotauditDashboardProvidersByUserId: {
      providesTags: (result, _error, { userId }) =>
        result
          ? [
              { type: 'Provider' as const, id: userId },
              { type: 'ProviderList' as const, id: 'LIST' },
            ]
          : [{ type: 'Provider', id: userId }],
    },
    getV2ProviderJotauditDashboardRulesByRuleIdAudits: {
      providesTags: (result, _error, { ruleId }) =>
        result && result.revisions
          ? [
              ...result.revisions.map(item => ({
                type: 'AuditRevision' as const,
                id: `${ruleId}-${item.revision}`,
              })),
              { type: 'AuditRevisionList' as const, id: ruleId },
            ]
          : [{ type: 'AuditRevisionList', id: ruleId }],
    },
    // Admin endpoints
    getV2AdminJotauditDashboardNotesByOrganizationId: {
      providesTags: (result, _error, { organizationId }) =>
        result && result.audits
          ? [
              ...result.audits.map(item => ({
                type: 'NoteAudit' as const,
                id: item.audit?.note_audit_id || 'unknown',
              })),
              { type: 'NoteAuditList' as const, id: organizationId },
            ]
          : [{ type: 'NoteAuditList', id: organizationId }],
    },
    getV2AdminJotauditDashboardProvidersByOrganizationId: {
      providesTags: (result, _error, { organizationId }) =>
        result && result.providers
          ? [
              ...result.providers.map(item => ({
                type: 'Provider' as const,
                id: item.user_id || 'unknown',
              })),
              { type: 'ProviderList' as const, id: organizationId },
            ]
          : [{ type: 'ProviderList', id: organizationId }],
    },
    getV2AdminJotauditDashboardRulesByRuleIdAudits: {
      providesTags: (result, _error, { pathRuleId }) =>
        result && result.revisions
          ? [
              ...result.revisions.map(item => ({
                type: 'AuditRevision' as const,
                id: `${pathRuleId}-${item.revision}`,
              })),
              { type: 'AuditRevisionList' as const, id: pathRuleId },
            ]
          : [{ type: 'AuditRevisionList', id: pathRuleId }],
    },
    // Global admin endpoints
    getV2AdminJotauditDashboardNotes: {
      providesTags: (result: any) =>
        result && result.audits
          ? [
              ...result.audits.map((item: any) => ({
                type: 'NoteAudit' as const,
                id: item.audit?.note_audit_id || 'unknown',
              })),
              { type: 'NoteAuditList' as const, id: 'GLOBAL' },
            ]
          : [{ type: 'NoteAuditList', id: 'GLOBAL' }],
    },
    getV2AdminJotauditDashboardOrganizations: {
      providesTags: (result: any) =>
        result && result.organizations
          ? [
              ...result.organizations.map((item: any) => ({
                type: 'Organization' as const,
                id: item.organization_id || 'unknown',
              })),
              { type: 'OrganizationList' as const, id: 'LIST' },
            ]
          : [{ type: 'OrganizationList', id: 'LIST' }],
    },
    getV2AdminJotauditDashboardProviders: {
      providesTags: (result: any) =>
        result && result.providers
          ? [
              ...result.providers.map((item: any) => ({
                type: 'Provider' as const,
                id: item.user_id || 'unknown',
              })),
              { type: 'ProviderList' as const, id: 'GLOBAL' },
            ]
          : [{ type: 'ProviderList', id: 'GLOBAL' }],
    },
    getV2AdminJotauditDashboardStats: {
      providesTags: [{ type: 'GlobalStats' as const, id: 'GLOBAL' }],
    },
    postV2AdminJotauditRun: {
      invalidatesTags: [
        { type: 'NoteAuditList', id: 'LIST' },
        { type: 'NoteAuditList', id: 'GLOBAL' },
        { type: 'GlobalStats', id: 'GLOBAL' },
      ],
    },
    postV2ProviderJotauditNotesByNoteIdReaudit: {
      invalidatesTags: (result, _error, { noteId }) => [
        { type: 'NoteAudit', id: noteId },
        { type: 'NoteAuditList', id: 'LIST' },
      ],
    },
  },
});

// Export hooks with user-friendly names
export const {
  // Provider endpoints
  useGetV2ProviderJotauditDashboardNotesQuery: useGetDashboardNotesQuery,
  useGetV2ProviderJotauditDashboardNotesByNoteIdQuery: useGetNoteAuditDetailQuery,
  useGetV2ProviderJotauditDashboardNotesByNoteIdHistoryQuery: useGetNoteAuditHistoryQuery,
  useGetV2ProviderJotauditDashboardProvidersQuery: useGetDashboardProvidersQuery,
  useGetV2ProviderJotauditDashboardProvidersByUserIdQuery: useGetProviderSummaryQuery,
  useGetV2ProviderJotauditDashboardRulesByRuleIdAuditsQuery: useGetRuleAuditsQuery,
  useGetV2ProviderJotauditDashboardStatsQuery: useGetDashboardStatsQuery,
  // Admin endpoints - Organization-specific
  useGetV2AdminJotauditDashboardNotesDetailByNoteIdQuery: useGetAdminNoteAuditDetailQuery,
  useGetV2AdminJotauditDashboardNotesDetailByNoteIdHistoryQuery: useGetAdminNoteAuditHistoryQuery,
  useGetV2AdminJotauditDashboardNotesDetailByNoteIdRevisionsAndRevisionQuery:
    useGetAdminNoteAuditRevisionQuery,
  useGetV2AdminJotauditDashboardNotesByOrganizationIdQuery: useGetAdminOrganizationNotesQuery,
  useGetV2AdminJotauditDashboardProvidersByOrganizationIdQuery:
    useGetAdminOrganizationProvidersQuery,
  useGetV2AdminJotauditDashboardProvidersByOrganizationIdAndUserIdQuery:
    useGetAdminProviderDetailQuery,
  useGetV2AdminJotauditDashboardRulesByRuleIdAuditsQuery: useGetAdminRuleAuditsQuery,
  useGetV2AdminJotauditDashboardStatsByOrganizationIdQuery: useGetAdminOrganizationStatsQuery,
  // Admin endpoints - Global (cross-organization)
  useGetV2AdminJotauditDashboardNotesQuery: useGetAdminGlobalNotesQuery,
  useGetV2AdminJotauditDashboardOrganizationsQuery: useGetAdminOrganizationsQuery,
  useGetV2AdminJotauditDashboardProvidersQuery: useGetAdminGlobalProvidersQuery,
  useGetV2AdminJotauditDashboardStatsQuery: useGetAdminGlobalStatsQuery,
  usePostV2AdminJotauditRunMutation: useRunAuditMutation,
  // Reaudit mutation
  usePostV2ProviderJotauditNotesByNoteIdReauditMutation: useReauditNoteMutation,
  // Status polling (lazy for manual control)
  useLazyGetV2ProviderJotauditStatusByThreadIdQuery: useLazyGetAuditStatusQuery,
} = jotauditApi;
